import { isEqual, orderBy } from "lodash";
import * as React from "react";
import { ClientService } from "../../client";
import { QESButton, QESMultiSelect, QESSelect } from "@app/shared/qes-standards";
import { AdminUsersContext, AdminUsersProps } from "../contexts/AdminUsersContext";
import { MultiselectItem, MultiselectOnChangeResponse } from "../../shared/qes-standards/forms/Multiselect";
import { CheckboxStateEnum } from "../../shared/qes-standards/forms/Checkbox";
import { RoleGroupModel, RoleGroupService } from "@app/admin/role-groups";
import { AccordionContainer, Accordion } from "@app/shared";
import { ColumnConfig, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { FlyoutContext, FlyoutContextOutgoingProps, FlyoutFooter } from "@app/shared/Flyouts";
import { JSX } from "react";

interface RolePreviewTableData {
  [wafflesKeyColumnName]: string;
  role: string;
  assignedBy: JSX.Element;
}

const rolePreviewColumnConfig: ColumnConfig = {
  assignedBy: {
    displayText: "Assigned By",
    testId: "assignedBy",
  },
  role: {
    displayText: "Role",
    testId: "role"
  },
};

interface GuestLoginAdminPanelProps {
  guestClientId?: number;
  guestClientName?: string;
  guestClientAssignedRoles?: string[];
  guestClientAssignedRoleGroups?: number[];
  guestUserId?: number;
}

interface GuestLoginAdminPanelState {
  clientOptions: JSX.Element[];
  availableRoleGroupOptions?: { [key: string]: MultiselectItem };
  availableRoleGroups?: RoleGroupModel[];
  availableRoles?: { [key: string]: MultiselectItem };
  availableRoleNames?: string[];
  resourceAdminRoles?: string[];
  rolePreview: RolePreviewTableData[];
  selectedClientId?: number;
  selectedRoleGroupIds?: number[];
  selectedRoles?: string[];
  questViewerRoles?: string[];
  userDefaultRoles?: string[];
}

type ContentProps = GuestLoginAdminPanelProps & Partial<AdminUsersProps> & FlyoutContextOutgoingProps;

class GuestLoginAdminPanelContent extends React.PureComponent<ContentProps, GuestLoginAdminPanelState> {
  constructor(props: ContentProps) {
    super(props);
    this.state = {
      availableRoleGroupOptions: {},
      availableRoleGroups: [],
      clientOptions: this.buildClientOptions(),
      rolePreview: [],
      selectedClientId: this.props.guestClientId,
      selectedRoleGroupIds: []
    };
  }

  private clientService = new ClientService();

  public componentDidMount() {
    if (this.props.guestClientId) {
      this.getAvailableClientRoles(this.props.guestClientId);
      this.updateGuestLoginRoles(this.props.guestClientId);
      this.getAvailableRoleGroups(this.props.guestClientId);
    }
  }

  public componentDidUpdate(prevProps: ContentProps, prevState: GuestLoginAdminPanelState) {
    if (this.props.guestClientName !== prevProps.guestClientName ||
      this.props.guestClientId !== prevProps.guestClientId ||
      !isEqual(this.props.allClients, prevProps.allClients)) {

      const newOptions = this.buildClientOptions();
      this.setState({
        clientOptions: newOptions,
        selectedClientId: this.props.guestClientId,
      });

      if (this.props.guestClientId) {
        this.getAvailableClientRoles(this.props.guestClientId);
        this.updateGuestLoginRoles(this.props.guestClientId);
        this.getAvailableRoleGroups(this.props.guestClientId);
      }
    }

    let updatePreview = false;
    if (prevState.selectedRoles !== this.state.selectedRoles && this.state.availableRoleNames) {
      const updatedRoleOptions: { [key: string]: MultiselectItem } = {};
      this.state.availableRoleNames?.forEach((name) => {
        updatedRoleOptions[name] = {
          currentState: this.state.selectedRoles.includes(name) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
          displayValue: name,
        }
      });
      this.setState({
        availableRoles: updatedRoleOptions,
      });
      updatePreview = true;
    }

    if (prevState.selectedRoleGroupIds !== this.state.selectedRoleGroupIds && this.state.availableRoleGroups) {
      const updatedRoleGroupOptions: { [key: string]: MultiselectItem } = {};
      this.state.availableRoleGroups?.forEach((rg) => {
        updatedRoleGroupOptions[rg.name] = {
          currentState: this.state.selectedRoleGroupIds.includes(rg.roleGroupId) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
          displayValue: rg.name,
        }
      });
      this.setState({
        availableRoleGroupOptions: updatedRoleGroupOptions,
      });
      updatePreview = true;
    }

    if (updatePreview) {
      this.createRolePreviewWafflesTable(this.state.selectedRoles, this.state.selectedRoleGroupIds);
    }
  }

  private buildClientOptions() {
    if (this.props.guestClientId) {
      // the user already exists and the client cannot be changed
      return [<option value={this.props.guestClientId} key={this.props.guestClientId}>{this.props.guestClientName}</option>];
    }
    // filter out inactive clients
    // filter out clientId 0 always and clientId 1 when editing guest logins for non-Quest users
    // filter out the current user's client id
    // filter out any clientids that already have current guest users
    return [<option value="-1" key="-1" />, ...this.props.allClients.filter((c) => c.isActive && c.clientId > (this.props.clientId === 1 ? 0 : 1) && c.clientId !== this.props.clientId && !this.props.guestLogins.find((gl) => gl.clientId === c.clientId)).map((client) => <option value={client.clientId} key={client.clientId}>{client.name}</option>)];
  }

  private createRolePreviewWafflesTable(selectedRoles: string[], selectedRoleGroupIds: number[]) {
    const roleDict: { [key: string]: { assignedBy: string[], name: string } } = {};
    if (!!selectedRoles) {
      for (const role of selectedRoles) {
        roleDict[role] = { assignedBy: ["Individually Assigned"], name: role };
      }
    }

    if (!!selectedRoleGroupIds) {
      const selectedRoleGroups = this.state.availableRoleGroups.filter(x => selectedRoleGroupIds.includes(x.roleGroupId));
      for (const roleGroup of orderBy(selectedRoleGroups, x => x.name, "asc")) {
        for (const role of roleGroup.roles) {
          if (!!roleDict[role.name]) {
            roleDict[role.name].assignedBy.push(roleGroup.name);
          } else {
            roleDict[role.name] = { assignedBy: [roleGroup.name], name: role.name };
          }
        }
      }
    }

    const preview: RolePreviewTableData[] = orderBy(Object.values(roleDict), rd => rd.name, "asc").map(rd => {
      /* eslint-disable sort-keys */
      return {
        key: rd.name,
        role: rd.name,
        assignedBy:
          <>
            {rd.assignedBy.map((x, i) => <div key={i}>{x}<br /></div>)}
          </>
      }
      /* eslint-enable sort-keys */
    });

    this.setState({ rolePreview: preview })
  }

  public render() {
    return (
      <>
        <div className="row mb-4">
          <QESSelect
            containerClassName="col-12"
            label="Client"
            onChange={this.handleClientChange}
            testId="guest-login__client"
            disabled={this.props.guestClientId > 0}
          >
            {this.state.clientOptions}
          </QESSelect>
        </div>
        {this.state.selectedClientId > 0 && this.state.availableRoles && (
          <div className="row">
            <div className="col-12 pl-0">
              <QESMultiSelect
                containerClassName="col-8 mb-3"
                label="Authorized Role Groups"
                onChange={this.updateRoleGroups}
                options={this.state.availableRoleGroupOptions}
                prefix="roleGroups"
                testId="guest-login__role-groups"
                validationTooltipContent="User must be in at least 1 role either directly or via a role group."
              />
              <QESMultiSelect
                containerClassName="col-8 mb-3"
                errorText={`At least one role or role group must be selected.`}
                label="Individually Assigned Roles"
                onChange={this.updateRoles}
                options={this.state.availableRoles}
                required={true}
                isValid={this.isRoleValid()}
                prefix="roles"
                testId="guest-login__roles"
                validationTooltipContent={`User must be in at least 1 role either directly or via a role group.`}
              />
              <QESButton
                qesTestId="default-useradmin-SelectAll"
                qesType="secondary"
                onClick={() => { this.setState((prevState) => ({ ...prevState, selectedRoles: this.state.availableRoleNames })) }}>
                Select All</QESButton>
              <QESButton
                qesTestId="default-useradmin-ClearAll"
                qesType="secondary"
                onClick={() => { this.setState((prevState) => ({ ...prevState, selectedRoles: Array<string>() })) }}>
                Clear All</QESButton>
              <QESButton
                qesTestId="default-useradmin-DefaultUser"
                qesType="secondary"
                onClick={() => { this.setState((prevState) => ({ ...prevState, selectedRoles: this.state.userDefaultRoles })) }}>
                Use Default User Roles</QESButton>
              <QESButton
                qesTestId="default-useradmin-ResourceAdminRoles"
                qesType="secondary"
                onClick={() => { this.setState((prevState) => ({ ...prevState, selectedRoles: this.state.resourceAdminRoles })) }}>
                Use Resource Admin Roles</QESButton>
              <QESButton
                qesTestId="default-useradmin-QuestViewerRoles"
                qesType="secondary"
                onClick={() => { this.setState((prevState) => ({ ...prevState, selectedRoles: this.state.questViewerRoles })) }}>
                Use Quest Viewer Roles</QESButton>
            </div>
          </div>
        )}
        <AccordionContainer className="mt-3">
          <Accordion title="Role Preview" testId="guest-login-admin-panel-content__role-preview-accordion">
            <Waffles<RolePreviewTableData>
              columns={rolePreviewColumnConfig}
              data={this.state.rolePreview}
              fullWidth={true}
              testId="role-preview-waffles-table"
              emptyTableMessage="There are no roles selected for this user."
            />
          </Accordion>
        </AccordionContainer>

        <FlyoutFooter
          submitButton={<QESButton qesTestId="guest-login-admin-panel-content_save" className="mt10" disabled={!this.isValid()} onClick={this.handleOnClick} > Save </QESButton>}
        />
      </>
    );
  }

  private isValid = () => {
    return this.state.selectedClientId && this.isRoleValid();
  }

  private isRoleValid = () => {
    return (this.state.selectedRoles && this.state.selectedRoles.length > 0) || (this.state.selectedRoleGroupIds && this.state.selectedRoleGroupIds.length > 0);
  }

  private handleOnClick = () => {
    if (this.props.guestClientId) {
      // update the roles for the already-existing guest user id
      this.props.updateGuestLogin(this.props.guestClientId, orderBy(this.state.selectedRoles, (r) => r.toLowerCase(), "asc"), this.state.selectedRoleGroupIds)

    } else {
      // add a guest login in the specified client to the specified user 
      this.props.addGuestLogin(this.state.selectedClientId, this.props.allClients.find((c) => c.clientId == this.state.selectedClientId).name, orderBy(this.state.selectedRoles, (r) => r.toLowerCase(), "asc"), this.state.selectedRoleGroupIds);
    }
    this.props.handleCloseFlyout();
  }

  // when the selected client changes, set the selectedClientId in state, load the roles for that client id and set those roles in state as well. 
  private handleClientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const clientId = parseInt(e.target.value, 10);

    this.setState((prevState) => ({
      ...prevState,
      selectedClientId: clientId,
    }));

    if (clientId) {
      // load all available and assigned roles for the selected client
      this.getAvailableClientRoles(clientId);
      this.updateGuestLoginRoles(clientId);
      this.getAvailableRoleGroups(clientId);
    }
  }

  // get the assigned roles for this client and set them in state
  private getAvailableClientRoles = (clientId: number) => {
    this.clientService.getClientRoles(clientId).then((roles) => {

      const roleOptions: { [key: string]: MultiselectItem } = {};
      const availableRoleNames: string[] = [];
      const userDefaultRoles: string[] = [];
      const resourceAdminRoles: string[] = [];
      const questViewerRoles: string[] = [];
      orderBy(roles, (r) => r.name.toLowerCase(), "asc").forEach((r) => {
        availableRoleNames.push(r.name);
        Object.assign(roleOptions, {
          [r.name]: {
            currentState: (this.state.selectedRoles && this.state.selectedRoles.find((sr) => sr === r.name)) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
            displayValue: r.name,
          },
        });
        if (r.userDefault === true)
          userDefaultRoles.push(r.name);
        if (r.resourceAdminGuestDefault === true)
          resourceAdminRoles.push(r.name);
        if (r.questViewerGuestDefault === true)
          questViewerRoles.push(r.name);
      });

      this.setState((prevState) => ({
        ...prevState,
        availableRoleNames,
        availableRoles: roleOptions,
        questViewerRoles,
        resourceAdminRoles,
        userDefaultRoles,
      }));
    });
  }

  private getAvailableRoleGroups = (clientId: number) => {
    const roleGroupsService = new RoleGroupService();
    roleGroupsService.getRoleGroupsByClientId(clientId, true, false, false, true, 0, -1).then((roleGroups) => {
      const roleGroupOptions: { [key: string]: MultiselectItem } = {};
      const availableRoleGroups: RoleGroupModel[] = [];

      orderBy(roleGroups.roleGroupList, (rg) => rg.name.toLowerCase(), "asc").forEach((rg) => {
        availableRoleGroups.push(rg);
        Object.assign(roleGroupOptions, {
          [rg.name]: {
            currentState: (this.state.selectedRoleGroupIds && this.state.selectedRoleGroupIds.find((srg) => srg === rg.roleGroupId)) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
            displayValue: rg.name,
          },
        });
      });

      let selectedRoleGroupIds: number[] = [];
      if (this.props.guestClientAssignedRoleGroups && clientId == this.props.guestClientId) {
        selectedRoleGroupIds = availableRoleGroups.filter(x => this.props.guestClientAssignedRoleGroups.includes(x.roleGroupId)).map(x => x.roleGroupId);
      }

      this.setState((prevState) => ({
        ...prevState,
        availableRoleGroupOptions: roleGroupOptions,
        availableRoleGroups: availableRoleGroups,
        selectedRoleGroupIds: selectedRoleGroupIds
      }));

      this.createRolePreviewWafflesTable(this.state.selectedRoles, selectedRoleGroupIds);
    });
  }

  // either get the currently assigned roles for the existing guest login or set an empty list of assigned roles if its a new guest login
  private updateGuestLoginRoles = (clientId: number) => {
    // if the guest client id is the selected client id, pre-load the currently selected roles (i.e., we are editing this guest login)
    let selectedRoles: string[];
    if (this.props.guestClientAssignedRoles && clientId == this.props.guestClientId) {
      selectedRoles = orderBy(this.props.guestClientAssignedRoles, (r) => r.toLowerCase(), "asc");
    } else { // clear the list of selected roles since we're adding a new guest login
      selectedRoles = this.state.userDefaultRoles;
    }

    this.setState((prevState) => ({
      ...prevState,
      selectedRoles: selectedRoles
    }));
    this.createRolePreviewWafflesTable(selectedRoles, this.state.selectedRoleGroupIds);
  }

  private updateRoles = (response: MultiselectOnChangeResponse) => {
    const selectedRoles = orderBy(response.checkedKeys, (r) => r.toLowerCase(), "asc");
    this.setState((prevState) => ({
      ...prevState,
      selectedRoles: selectedRoles,
    }));
  }

  private updateRoleGroups = (response: MultiselectOnChangeResponse) => {
    const selectedRoleGroupOptions = response.checkedKeys;
    const selectedRoleGroupIds = this.state.availableRoleGroups.filter(x => selectedRoleGroupOptions.includes(x.name)).map(x => x.roleGroupId);
    this.setState((prevState) => ({
      ...prevState,
      selectedRoleGroupIds: selectedRoleGroupIds,
    }));
  }
}

class WrappedGuestLoginAdminPanelContent extends React.PureComponent<GuestLoginAdminPanelProps> {
  render() {
    return (
      <FlyoutContext.Consumer>
        {(flyoutProps) => (
          <AdminUsersContext.Consumer>
            {(contextProps) => (
              <GuestLoginAdminPanelContent {...this.props} {...contextProps} {...flyoutProps} />
            )}
          </AdminUsersContext.Consumer>
        )}
      </FlyoutContext.Consumer>
    )
  }
}

export { WrappedGuestLoginAdminPanelContent as GuestLoginAdminPanelContent };
